import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Loader2, Plus, Edit, Trash2, X, Percent, DollarSign, Calendar, Tag, Package
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../AuthContext';
import { taxesApi, promotionsApi, addonsApi } from '../../services/api';
import { supabase } from '../../supabaseClient';

const AdminBillingPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('taxes');
  const [taxes, setTaxes] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [addons, setAddons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [token, setToken] = useState(null);

  // Tax form
  const [taxForm, setTaxForm] = useState({
    name: '',
    rate_percent: '',
    is_active: true,
    apply_order: 1
  });

  // Promotion form
  const [promoForm, setPromoForm] = useState({
    code: '',
    name: '',
    description: '',
    discount_type: 'PERCENT',
    discount_value: '',
    start_date: '',
    end_date: '',
    is_active: true,
    stackable: false,
    code_required: true
  });

  // Add-on form
  const [addonForm, setAddonForm] = useState({
    name: '',
    description: '',
    pricing_type: 'PER_PERSON',
    unit_price: '',
    per_night: false,
    is_active: true,
    sort_order: 1
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const authToken = sessionData?.session?.access_token;
      
      if (!authToken) {
        navigate('/login');
        return;
      }
      
      setToken(authToken);
      
      const [taxData, promoData, addonData] = await Promise.all([
        taxesApi.getAll(false),
        promotionsApi.getAll(false),
        addonsApi.getAll(false)
      ]);
      
      setTaxes(taxData);
      setPromotions(promoData);
      setAddons(addonData);
    } catch (err) {
      console.error('Error fetching billing data:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to load billing data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate, fetchData]);

  // Tax handlers
  const handleCreateTax = () => {
    setEditing(null);
    setTaxForm({
      name: '',
      rate_percent: '',
      is_active: true,
      apply_order: taxes.length + 1
    });
    setShowForm(true);
  };

  const handleEditTax = (tax) => {
    setEditing(tax.id);
    setTaxForm({
      name: tax.name || '',
      rate_percent: tax.rate_percent || '',
      is_active: tax.is_active !== false,
      apply_order: tax.apply_order || 1
    });
    setShowForm(true);
  };

  const handleSubmitTax = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const data = {
        name: taxForm.name,
        rate_percent: parseFloat(taxForm.rate_percent) || 0,
        is_active: taxForm.is_active,
        apply_order: parseInt(taxForm.apply_order) || 1
      };
      
      if (editing) {
        await taxesApi.update(editing, data, token);
        toast({ title: "Success", description: "Tax updated successfully" });
      } else {
        await taxesApi.create(data, token);
        toast({ title: "Success", description: "Tax created successfully" });
      }
      
      setShowForm(false);
      setEditing(null);
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to save tax",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTax = async (id) => {
    if (!window.confirm('Delete this tax configuration?')) return;
    
    try {
      await taxesApi.delete(id, token);
      toast({ title: "Success", description: "Tax deleted" });
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete tax",
        variant: "destructive"
      });
    }
  };

  // Promotion handlers
  const handleCreatePromo = () => {
    setEditing(null);
    const today = new Date().toISOString().split('T')[0];
    const nextMonth = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setPromoForm({
      code: '',
      name: '',
      description: '',
      discount_type: 'PERCENT',
      discount_value: '',
      start_date: today,
      end_date: nextMonth,
      is_active: true,
      stackable: false,
      code_required: true
    });
    setShowForm(true);
  };

  const handleEditPromo = (promo) => {
    setEditing(promo.id);
    setPromoForm({
      code: promo.code || '',
      name: promo.name || '',
      description: promo.description || '',
      discount_type: promo.discount_type || 'PERCENT',
      discount_value: promo.discount_value || '',
      start_date: promo.start_date || '',
      end_date: promo.end_date || '',
      is_active: promo.is_active !== false,
      stackable: promo.stackable === true,
      code_required: promo.code_required !== false
    });
    setShowForm(true);
  };

  const handleSubmitPromo = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const data = {
        code: promoForm.code || null,
        name: promoForm.name,
        description: promoForm.description,
        discount_type: promoForm.discount_type,
        discount_value: parseFloat(promoForm.discount_value) || 0,
        start_date: promoForm.start_date,
        end_date: promoForm.end_date,
        is_active: promoForm.is_active,
        stackable: promoForm.stackable,
        code_required: promoForm.code_required
      };
      
      if (editing) {
        await promotionsApi.update(editing, data, token);
        toast({ title: "Success", description: "Promotion updated successfully" });
      } else {
        await promotionsApi.create(data, token);
        toast({ title: "Success", description: "Promotion created successfully" });
      }
      
      setShowForm(false);
      setEditing(null);
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to save promotion",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePromo = async (id) => {
    if (!window.confirm('Delete this promotion?')) return;
    
    try {
      await promotionsApi.delete(id, token);
      toast({ title: "Success", description: "Promotion deleted" });
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete promotion",
        variant: "destructive"
      });
    }
  };

  // Add-on handlers
  const handleCreateAddon = () => {
    setEditing(null);
    setAddonForm({
      name: '',
      description: '',
      pricing_type: 'PER_PERSON',
      unit_price: '',
      per_night: false,
      is_active: true,
      sort_order: addons.length + 1
    });
    setShowForm(true);
  };

  const handleEditAddon = (addon) => {
    setEditing(addon.id);
    setAddonForm({
      name: addon.name || '',
      description: addon.description || '',
      pricing_type: addon.pricing_type || 'PER_PERSON',
      unit_price: addon.unit_price || '',
      per_night: addon.per_night === true,
      is_active: addon.is_active !== false,
      sort_order: addon.sort_order || 1
    });
    setShowForm(true);
  };

  const handleSubmitAddon = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const data = {
        name: addonForm.name,
        description: addonForm.description,
        pricing_type: addonForm.pricing_type,
        unit_price: parseFloat(addonForm.unit_price) || 0,
        per_night: addonForm.per_night,
        is_active: addonForm.is_active,
        sort_order: parseInt(addonForm.sort_order) || 1
      };
      
      if (editing) {
        await addonsApi.update(editing, data, token);
        toast({ title: "Success", description: "Add-on updated successfully" });
      } else {
        await addonsApi.create(data, token);
        toast({ title: "Success", description: "Add-on created successfully" });
      }
      
      setShowForm(false);
      setEditing(null);
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to save add-on",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAddon = async (id) => {
    if (!window.confirm('Delete this add-on?')) return;
    
    try {
      await addonsApi.delete(id, token);
      toast({ title: "Success", description: "Add-on deleted" });
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete add-on",
        variant: "destructive"
      });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
  };

  if (!isAuthenticated) return null;

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }} data-testid="admin-billing">
      <div className="container py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="outline" size="icon" style={{ borderRadius: 0 }}>
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="hero-medium">Taxes & Promotions</h1>
            <p className="body-small mt-1" style={{ color: 'var(--text-secondary)' }}>
              Configure tax rules and promotional discounts
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button 
            variant={activeTab === 'taxes' ? 'default' : 'outline'}
            onClick={() => { setActiveTab('taxes'); setShowForm(false); }}
            style={{ borderRadius: 0 }}
            className={activeTab === 'taxes' ? 'btn-primary' : ''}
          >
            <Percent size={16} className="mr-2" />
            Taxes ({taxes.length})
          </Button>
          <Button 
            variant={activeTab === 'promotions' ? 'default' : 'outline'}
            onClick={() => { setActiveTab('promotions'); setShowForm(false); }}
            style={{ borderRadius: 0 }}
            className={activeTab === 'promotions' ? 'btn-primary' : ''}
          >
            <Tag size={16} className="mr-2" />
            Promotions ({promotions.length})
          </Button>
          <Button 
            variant={activeTab === 'addons' ? 'default' : 'outline'}
            onClick={() => { setActiveTab('addons'); setShowForm(false); }}
            style={{ borderRadius: 0 }}
            className={activeTab === 'addons' ? 'btn-primary' : ''}
          >
            <Package size={16} className="mr-2" />
            Add-ons ({addons.length})
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin" size={32} style={{ color: 'var(--olive-primary)' }} />
          </div>
        ) : (
          <>
            {/* TAXES TAB */}
            {activeTab === 'taxes' && (
              <>
                {/* Add Tax Button */}
                <div className="mb-6">
                  <Button className="btn-primary flex items-center gap-2" onClick={handleCreateTax} data-testid="add-tax-btn">
                    <Plus size={16} />
                    Add Tax
                  </Button>
                </div>

                {/* Tax Form Dialog */}
                <Dialog open={showForm && activeTab === 'taxes'} onOpenChange={(open) => { if (!open) handleCancel(); }}>
                  <DialogContent className="sm:max-w-lg" style={{ borderRadius: 0, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
                    <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4" style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <DialogTitle>{editing ? 'Edit Tax' : 'Add Tax'}</DialogTitle>
                      <DialogDescription>Configure tax rate and application order.</DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmitTax} className="flex flex-col flex-1 overflow-hidden">
                      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="tax_name">Tax Name *</Label>
                            <Input
                              id="tax_name"
                              value={taxForm.name}
                              onChange={(e) => setTaxForm({ ...taxForm, name: e.target.value })}
                              required
                              placeholder="e.g., ZTA, VAT, Service Tax"
                              style={{ borderRadius: 0 }}
                              data-testid="tax-name-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="rate_percent">Rate (%) *</Label>
                            <Input
                              id="rate_percent"
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={taxForm.rate_percent}
                              onChange={(e) => setTaxForm({ ...taxForm, rate_percent: e.target.value })}
                              required
                              style={{ borderRadius: 0 }}
                              data-testid="tax-rate-input"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="apply_order">Apply Order</Label>
                            <Input
                              id="apply_order"
                              type="number"
                              min="1"
                              value={taxForm.apply_order}
                              onChange={(e) => setTaxForm({ ...taxForm, apply_order: e.target.value })}
                              style={{ borderRadius: 0 }}
                            />
                            <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                              Order in which taxes are applied (1 = first)
                            </p>
                          </div>
                          <div className="flex items-center gap-2 pt-6">
                            <input
                              type="checkbox"
                              id="tax_active"
                              checked={taxForm.is_active}
                              onChange={(e) => setTaxForm({ ...taxForm, is_active: e.target.checked })}
                            />
                            <Label htmlFor="tax_active" className="cursor-pointer">Active</Label>
                          </div>
                        </div>
                      </div>

                      <div className="flex-shrink-0 flex justify-between px-6 py-4" style={{ borderTop: '1px solid var(--border-light)' }}>
                        <Button type="button" variant="outline" onClick={handleCancel} style={{ borderRadius: 0 }}>
                          Cancel
                        </Button>
                        <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-tax-btn">
                          {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                          {editing ? 'Update Tax' : 'Add Tax'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Taxes List */}
                {taxes.length === 0 ? (
                  <div className="p-12 text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
                    <p className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>
                      No taxes configured. Add your first tax (e.g., ZTA at 2%).
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="taxes-list">
                    {taxes.map((tax) => (
                      <div
                        key={tax.id}
                        className="p-4 flex items-center justify-between"
                        style={{ 
                          backgroundColor: 'var(--bg-secondary)', 
                          border: '1px solid var(--border-light)',
                          opacity: tax.is_active ? 1 : 0.6
                        }}
                        data-testid={`tax-${tax.id}`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{tax.name}</span>
                            {!tax.is_active && (
                              <span className="px-2 py-0.5 text-xs" style={{ backgroundColor: '#ef4444', color: 'white' }}>
                                Inactive
                              </span>
                            )}
                          </div>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {tax.rate_percent}% • Order: {tax.apply_order}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={() => handleEditTax(tax)}
                            style={{ borderRadius: 0 }}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={() => handleDeleteTax(tax.id)}
                            style={{ borderRadius: 0, borderColor: '#ef4444', color: '#ef4444' }}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* PROMOTIONS TAB */}
            {activeTab === 'promotions' && (
              <>
                {/* Add Promotion Button */}
                <div className="mb-6">
                  <Button className="btn-primary flex items-center gap-2" onClick={handleCreatePromo} data-testid="add-promo-btn">
                    <Plus size={16} />
                    Add Promotion
                  </Button>
                </div>

                {/* Promotion Form Dialog */}
                <Dialog open={showForm && activeTab === 'promotions'} onOpenChange={(open) => { if (!open) handleCancel(); }}>
                  <DialogContent className="sm:max-w-xl" style={{ borderRadius: 0, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
                    <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4" style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <DialogTitle>{editing ? 'Edit Promotion' : 'Add Promotion'}</DialogTitle>
                      <DialogDescription>Configure promotional discount details.</DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmitPromo} className="flex flex-col flex-1 overflow-hidden">
                      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="promo_name">Promotion Name *</Label>
                          <Input
                            id="promo_name"
                            value={promoForm.name}
                            onChange={(e) => setPromoForm({ ...promoForm, name: e.target.value })}
                            required
                            placeholder="e.g., Summer Special"
                            style={{ borderRadius: 0 }}
                            data-testid="promo-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="promo_code">Promo Code</Label>
                          <Input
                            id="promo_code"
                            value={promoForm.code}
                            onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                            placeholder="e.g., SUMMER25 (optional)"
                            style={{ borderRadius: 0 }}
                            data-testid="promo-code-input"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="promo_description">Description</Label>
                        <Input
                          id="promo_description"
                          value={promoForm.description}
                          onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                          placeholder="Optional description"
                          style={{ borderRadius: 0 }}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="discount_type">Discount Type *</Label>
                          <select
                            id="discount_type"
                            value={promoForm.discount_type}
                            onChange={(e) => setPromoForm({ ...promoForm, discount_type: e.target.value })}
                            className="w-full h-10 px-3 border"
                            style={{ borderColor: 'var(--border-medium)', borderRadius: 0 }}
                          >
                            <option value="PERCENT">Percentage Off</option>
                            <option value="FIXED">Fixed Amount Off</option>
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="discount_value">
                            Discount Value ({promoForm.discount_type === 'PERCENT' ? '%' : '$'}) *
                          </Label>
                          <Input
                            id="discount_value"
                            type="number"
                            step="0.01"
                            min="0"
                            value={promoForm.discount_value}
                            onChange={(e) => setPromoForm({ ...promoForm, discount_value: e.target.value })}
                            required
                            style={{ borderRadius: 0 }}
                            data-testid="discount-value-input"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="start_date">Start Date *</Label>
                          <Input
                            id="start_date"
                            type="date"
                            value={promoForm.start_date}
                            onChange={(e) => setPromoForm({ ...promoForm, start_date: e.target.value })}
                            required
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div>
                          <Label htmlFor="end_date">End Date *</Label>
                          <Input
                            id="end_date"
                            type="date"
                            value={promoForm.end_date}
                            onChange={(e) => setPromoForm({ ...promoForm, end_date: e.target.value })}
                            required
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-3">
                        <div className="flex gap-6">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="promo_active"
                              checked={promoForm.is_active}
                              onChange={(e) => setPromoForm({ ...promoForm, is_active: e.target.checked })}
                            />
                            <Label htmlFor="promo_active" className="cursor-pointer">Active</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="promo_stackable"
                              checked={promoForm.stackable}
                              onChange={(e) => setPromoForm({ ...promoForm, stackable: e.target.checked })}
                            />
                            <Label htmlFor="promo_stackable" className="cursor-pointer">Stackable (can combine with other promos)</Label>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="promo_code_required"
                            checked={promoForm.code_required}
                            onChange={(e) => setPromoForm({ ...promoForm, code_required: e.target.checked })}
                            data-testid="promo-code-required-input"
                          />
                          <Label htmlFor="promo_code_required" className="cursor-pointer">Require promo code</Label>
                          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            (when unchecked, discount auto-applies to all bookings during the promotion period)
                          </span>
                        </div>
                      </div>

                      </div>

                      <div className="flex-shrink-0 flex justify-between px-6 py-4" style={{ borderTop: '1px solid var(--border-light)' }}>
                        <Button type="button" variant="outline" onClick={handleCancel} style={{ borderRadius: 0 }}>
                          Cancel
                        </Button>
                        <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-promo-btn">
                          {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                          {editing ? 'Update Promotion' : 'Add Promotion'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Promotions List */}
                {promotions.length === 0 ? (
                  <div className="p-12 text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
                    <p className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>
                      No promotions configured. Create your first promotional offer!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="promotions-list">
                    {promotions.map((promo) => {
                      const isExpired = new Date(promo.end_date) < new Date();
                      const notStarted = new Date(promo.start_date) > new Date();
                      
                      return (
                        <div
                          key={promo.id}
                          className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4"
                          style={{ 
                            backgroundColor: 'var(--bg-secondary)', 
                            border: '1px solid var(--border-light)',
                            opacity: promo.is_active && !isExpired ? 1 : 0.6
                          }}
                          data-testid={`promo-${promo.id}`}
                        >
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{promo.name}</span>
                              {promo.code && (
                                <span className="px-2 py-0.5 text-xs font-mono" style={{ backgroundColor: 'var(--olive-primary)', color: 'white' }}>
                                  {promo.code}
                                </span>
                              )}
                              {promo.code_required === false && (
                                <span className="px-2 py-0.5 text-xs" style={{ backgroundColor: '#8b5cf6', color: 'white' }}>
                                  Auto-apply
                                </span>
                              )}
                              {!promo.is_active && (
                                <span className="px-2 py-0.5 text-xs" style={{ backgroundColor: '#ef4444', color: 'white' }}>
                                  Inactive
                                </span>
                              )}
                              {isExpired && (
                                <span className="px-2 py-0.5 text-xs" style={{ backgroundColor: '#9ca3af', color: 'white' }}>
                                  Expired
                                </span>
                              )}
                              {notStarted && (
                                <span className="px-2 py-0.5 text-xs" style={{ backgroundColor: '#3b82f6', color: 'white' }}>
                                  Scheduled
                                </span>
                              )}
                            </div>
                            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                              {promo.discount_type === 'PERCENT' ? `${promo.discount_value}% off` : `$${promo.discount_value} off`}
                              {' • '}
                              {promo.start_date} to {promo.end_date}
                              {promo.stackable && ' • Stackable'}
                            </p>
                            {promo.description && (
                              <p className="text-xs mt-1" style={{ color: 'var(--text-light)' }}>
                                {promo.description}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="icon" 
                              onClick={() => handleEditPromo(promo)}
                              style={{ borderRadius: 0 }}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="icon" 
                              onClick={() => handleDeletePromo(promo.id)}
                              style={{ borderRadius: 0, borderColor: '#ef4444', color: '#ef4444' }}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* ADD-ONS TAB */}
            {activeTab === 'addons' && (
              <>
                {/* Add Add-on Button */}
                <div className="mb-6">
                  <Button className="btn-primary flex items-center gap-2" onClick={handleCreateAddon} data-testid="add-addon-btn">
                    <Plus size={16} />
                    Add Add-on
                  </Button>
                </div>

                {/* Add-on Form Dialog */}
                <Dialog open={showForm && activeTab === 'addons'} onOpenChange={(open) => { if (!open) handleCancel(); }}>
                  <DialogContent className="sm:max-w-lg" style={{ borderRadius: 0, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
                    <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4" style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <DialogTitle>{editing ? 'Edit Add-on' : 'Add Add-on'}</DialogTitle>
                      <DialogDescription>Configure add-on service details and pricing.</DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmitAddon} className="flex flex-col flex-1 overflow-hidden">
                      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                        <div>
                          <Label htmlFor="addon_name">Name *</Label>
                          <Input
                            id="addon_name"
                            value={addonForm.name}
                            onChange={(e) => setAddonForm({ ...addonForm, name: e.target.value })}
                            required
                            placeholder="e.g., Breakfast, Airport Shuttle"
                            style={{ borderRadius: 0 }}
                            data-testid="addon-name-input"
                          />
                        </div>

                        <div>
                          <Label htmlFor="addon_description">Description</Label>
                          <Input
                            id="addon_description"
                            value={addonForm.description}
                            onChange={(e) => setAddonForm({ ...addonForm, description: e.target.value })}
                            placeholder="Optional description of the service"
                            style={{ borderRadius: 0 }}
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="pricing_type">Pricing Type *</Label>
                            <select
                              id="pricing_type"
                              value={addonForm.pricing_type}
                              onChange={(e) => setAddonForm({ ...addonForm, pricing_type: e.target.value })}
                              className="w-full h-10 px-3 border"
                              style={{ borderColor: 'var(--border-medium)', borderRadius: 0 }}
                            >
                              <option value="PER_PERSON">Per Person</option>
                              <option value="FLAT">Flat Fee</option>
                            </select>
                            <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                              {addonForm.pricing_type === 'PER_PERSON' 
                                ? 'Price multiplied by number of people' 
                                : 'Fixed price regardless of group size'}
                            </p>
                          </div>
                          <div>
                            <Label htmlFor="unit_price">Unit Price ($) *</Label>
                            <Input
                              id="unit_price"
                              type="number"
                              step="0.01"
                              min="0"
                              value={addonForm.unit_price}
                              onChange={(e) => setAddonForm({ ...addonForm, unit_price: e.target.value })}
                              required
                              style={{ borderRadius: 0 }}
                              data-testid="addon-price-input"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="addon_sort_order">Sort Order</Label>
                            <Input
                              id="addon_sort_order"
                              type="number"
                              min="1"
                              value={addonForm.sort_order}
                              onChange={(e) => setAddonForm({ ...addonForm, sort_order: e.target.value })}
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                          <div className="flex flex-col gap-3 pt-6">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="addon_per_night"
                                checked={addonForm.per_night}
                                onChange={(e) => setAddonForm({ ...addonForm, per_night: e.target.checked })}
                              />
                              <Label htmlFor="addon_per_night" className="cursor-pointer">Charge per night</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="addon_active"
                                checked={addonForm.is_active}
                                onChange={(e) => setAddonForm({ ...addonForm, is_active: e.target.checked })}
                              />
                              <Label htmlFor="addon_active" className="cursor-pointer">Active</Label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex-shrink-0 flex justify-between px-6 py-4" style={{ borderTop: '1px solid var(--border-light)' }}>
                        <Button type="button" variant="outline" onClick={handleCancel} style={{ borderRadius: 0 }}>
                          Cancel
                        </Button>
                        <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-addon-btn">
                          {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                          {editing ? 'Update Add-on' : 'Add Add-on'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Add-ons List */}
                {addons.length === 0 ? (
                  <div className="p-12 text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
                    <p className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>
                      No add-ons configured. Add services like breakfast or airport shuttle.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="addons-list">
                    {addons.map((addon) => (
                      <div
                        key={addon.id}
                        className="p-4 flex items-center justify-between"
                        style={{ 
                          backgroundColor: 'var(--bg-secondary)', 
                          border: '1px solid var(--border-light)',
                          opacity: addon.is_active ? 1 : 0.6
                        }}
                        data-testid={`addon-${addon.id}`}
                      >
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{addon.name}</span>
                            <span className="px-2 py-0.5 text-xs" style={{ 
                              backgroundColor: addon.pricing_type === 'PER_PERSON' ? 'var(--olive-primary)' : '#3b82f6', 
                              color: 'white' 
                            }}>
                              {addon.pricing_type === 'PER_PERSON' ? 'Per Person' : 'Flat Fee'}
                            </span>
                            {addon.per_night && (
                              <span className="px-2 py-0.5 text-xs" style={{ backgroundColor: '#8b5cf6', color: 'white' }}>
                                Per Night
                              </span>
                            )}
                            {!addon.is_active && (
                              <span className="px-2 py-0.5 text-xs" style={{ backgroundColor: '#ef4444', color: 'white' }}>
                                Inactive
                              </span>
                            )}
                          </div>
                          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                            ${addon.unit_price}
                            {addon.pricing_type === 'PER_PERSON' ? ' / person' : ' flat'}
                            {addon.per_night ? ' / night' : ''}
                            {' • Order: '}{addon.sort_order}
                          </p>
                          {addon.description && (
                            <p className="text-xs mt-1" style={{ color: 'var(--text-light)' }}>
                              {addon.description}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={() => handleEditAddon(addon)}
                            style={{ borderRadius: 0 }}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={() => handleDeleteAddon(addon.id)}
                            style={{ borderRadius: 0, borderColor: '#ef4444', color: '#ef4444' }}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminBillingPage;
